import './styles.scss';

import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

import { useContext, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import TokenDataWidget from '../v2/widgets/token-data';
import VolatilitySurfaceWidget from '../v2/widgets/volatility-surface/volatility-surface-widget';
import MarketHistoryWidget from '../v2/widgets/market-history';
import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { MarketDataProvider } from '../../providers/market-data/market-data-provider';
import { OverviewContext } from '../../providers/overview-provider';
import { SnapshotProvider } from '../../providers/snapshot-provider';
import { VolMarketProvider } from '../../providers/vol-market-provider';

import ResetDataButton from './reset-data-button';
import { RestoreLayoutButton } from './restore-layout-button';
import { WidgetKeys } from '../../providers/model/custom-widget';

const ResponsiveGridLayout = WidthProvider(Responsive);

type OverviewProps = {
  currency: CurrencyTypeEnum;
};

export default function Overview({ currency }: OverviewProps) {
  const { layouts, setLayouts, rowExpanded, toggleRowExpand } = useContext(OverviewContext);

  const renderBody = useMemo(() => {
    const widgetClassName = (token: string) => {
      if (!rowExpanded || rowExpanded.size === 0) return '';
      return rowExpanded.has(token) ? 'row-maximized' : '';
    };

    const handleLayoutChange = (
      layout: ReactGridLayout.Layout[],
      layouts: ReactGridLayout.Layouts,
    ) => {
      setLayouts(layouts);
    };

    return (
      <div className="main-content">
        <ResponsiveGridLayout
          className="grid"
          layouts={layouts}
          autoSize={true}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
          resizeHandles={['se']}
          draggableHandle=".drag-area"
          onLayoutChange={handleLayoutChange}
          useCSSTransforms={true}
        >
          <div key="market-history" className={widgetClassName('market-history')}>
            <MarketHistoryWidget
              actions={{
                handleMaximizeWidget: toggleRowExpand,
              }}
              widget={{
                key: WidgetKeys.MarketHistory,
                title: 'Market History',
                id: 'market-history',
                isMaximized: rowExpanded.has('market-history'),
              }}
              permissions={{ canDelete: false, canDuplicate: false, canChangeToken: false }}
            />
          </div>

          <div key="snapshot-data" className={widgetClassName('snapshot-data')}>
            <VolatilitySurfaceWidget
              actions={{
                handleMaximizeWidget: toggleRowExpand,
              }}
              widget={{
                key: WidgetKeys.VolSurface,
                title: 'Vol Surface',
                id: 'snapshot-data',
                isMaximized: rowExpanded.has('snapshot-data'),
              }}
              permissions={{ canDelete: false, canDuplicate: false, canChangeToken: false }}
            />
          </div>
        </ResponsiveGridLayout>
      </div>
    );
  }, [layouts, rowExpanded, setLayouts]);

  return (
    <div className="dashboard-container">
      <div key="token-config" className="token-selector">
        <TokenDataWidget currency={currency} />
      </div>
      <MarketDataProvider currency={currency as CurrencyTypeEnum}>
        <SnapshotProvider tokenKey={currency}>
          <VolMarketProvider>
            {renderBody}
            <div className="layout-buttons">
              <ResetDataButton />
              <RestoreLayoutButton />
            </div>
          </VolMarketProvider>
        </SnapshotProvider>
      </MarketDataProvider>
    </div>
  );
}
